import { useState } from "react";
import "./PlannerFlowChart.scss";

/**
 * Static reference diagram of the planner ENGINE — how generateTrainingPlan()
 * turns a race + profile into a scheduled, structured plan, and how publish
 * reconciles it with intervals.icu. Read-only; collapsed by default. Each node
 * names the function that does the work so it can be checked against the code
 * (COMPONENTS/planGenerator.ts, COMPONENTS/workoutBank.ts, COMPONENTS/publishPlan.ts).
 */

interface FlowNode {
  n: number;
  title: string;
  fn: string;
  desc: string;
  accent: "base" | "build" | "peak" | "taper" | "cutback";
}

const ENGINE: FlowNode[] = [
  { n: 1,  title: "Indata", fn: "generateTrainingPlan(opts)", accent: "base",
    desc: "Lopp (distans i km & måltid), nuvarande CTL/TSB, profil (dagar, maxtimmar, sporter) och historik (veckovolym & längsta pass)." },
  { n: 2,  title: "Lopp → långpasstak & distansprofil", fn: "expectedHoursFromKm / kmToDistanceBand", accent: "base",
    desc: "Måltid (parseGoalHours) eller distans i km ger förväntad loppttid som skalar långpasset steglöst (tak 4 h); distansbandet (5k…ultra) väljer interval-IF och passbeskrivningar. Loppnamnet gissas bara som sista utväg." },
  { n: 3,  title: "Fasindelning", fn: "phases[]", accent: "build",
    desc: "Veckorna fram till loppet delas i Bas → Bygg → Peak → Taper, med en cutback-vecka var fjärde." },
  { n: 4,  title: "Specificitet per vecka", fn: "weekSpecificity", accent: "build",
    desc: "Generell → tröskel → race, ju närmare loppet (Bompa/Daniels specificitetsprincip)." },
  { n: 5,  title: "Veckovolym i timmar", fn: "weeklyHours[]", accent: "build",
    desc: "CTL-styrd ramp. Skalas ned för semester (blockerade dagar) och återgång efter sjukdom." },
  { n: 6,  title: "Sessionsmallar", fn: "buildTemplates", accent: "cutback",
    desc: "Long / kvalitet / lätt / gym med TSS-andelar. 1–2 kvalitetspass beroende på CTL." },
  { n: 7,  title: "Fördela timmar", fn: "allocateWeek", accent: "cutback",
    desc: "Polariserat: long + kvalitet capas först, lätt löpning fyller resten (~80/20)." },
  { n: 8,  title: "Placera passen", fn: "makeSession", accent: "peak",
    desc: "På föredragna dagar; hoppar över blockerade/passerade dagar och lopp; buffert runt hårda pass; säsongsbyte (skidor på vintern)." },
  { n: 9,  title: "Välj kvalitetspass ur banken", fn: "pickIntervalSession", accent: "peak",
    desc: "Fokus (aerob/tröskel/VO2max/race) × sport/distans/CTL → ett strukturerat intervals.icu-pass. Egna pass ingår i banken och stjärnmärkta favoriter föredras inom sin fas (se Intervallbank)." },
  { n: 10, title: "Fasta pass, gym & pendling", fn: "fixedSessions / gym / commute", accent: "taper",
    desc: "Vävs in i veckan eller budgeteras mot volymen utan att stökas ned på kalendern." },
  { n: 11, title: "Resultat", fn: "{ weeks, sessions, … }", accent: "taper",
    desc: "Veckor (mål-TSS/tid), pass (datum/namn/sport/beskrivning/TSS), flaggor, förväntad TSB på loppdagen och hård-%." },
];

export function PlannerFlowChart() {
  const [open, setOpen] = useState(false);

  return (
    <section className="pfc">
      <button className={`pfc__toggle${open ? " pfc__toggle--open" : ""}`} onClick={() => setOpen(o => !o)} aria-expanded={open}>
        <span className="pfc__toggle-caret">{open ? "▾" : "▸"}</span>
        Så funkar planeraren
        <span className="pfc__toggle-hint">motorns flöde</span>
      </button>

      {open && (
        <div className="pfc__body">
          <ol className="pfc__flow">
            {ENGINE.map((node, i) => (
              <li key={node.n} className="pfc__node-wrap">
                <div className={`pfc__node pfc__node--${node.accent}`}>
                  <div className="pfc__node-head">
                    <span className="pfc__node-num">{node.n}</span>
                    <span className="pfc__node-title">{node.title}</span>
                    <code className="pfc__node-fn">{node.fn}</code>
                  </div>
                  <div className="pfc__node-desc">{node.desc}</div>
                </div>
                {i < ENGINE.length - 1 && <div className="pfc__arrow" aria-hidden="true">↓</div>}
              </li>
            ))}
          </ol>

          <div className="pfc__arrow pfc__arrow--publish" aria-hidden="true">↓ publicering</div>

          <div className="pfc__node pfc__node--publish">
            <div className="pfc__node-head">
              <span className="pfc__node-num">→</span>
              <span className="pfc__node-title">Publicering till intervals.icu</span>
              <code className="pfc__node-fn">publishPlan</code>
            </div>
            <div className="pfc__node-desc">
              Jämför planen mot kalendern och skapar/raderar bara det som ändrats — oförändrade pass lämnas orörda, lopp tas aldrig bort.
            </div>
          </div>

          <p className="pfc__footnote">
            Statiskt referensdiagram. Funktionsnamnen pekar på koden i <code>planGenerator.ts</code>, <code>workoutBank.ts</code> och <code>publishPlan.ts</code>.
          </p>
        </div>
      )}
    </section>
  );
}
